import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { UserProfile } from '@domain/ports/IProfileGateway';
import type { StoreExtraDependencies } from '../index';
import type { RootState } from '../index';

/**
 * Profile State Interface
 */
export interface ProfileState {
  data: UserProfile | null;
  isLoading: boolean;
  uploadProgress: number; // 0-100
  error: string | null;
  isExporting: boolean; // GDPR data export in progress
  isDeleting: boolean;  // Account deletion in progress
}

/**
 * Initial State
 */
const initialState: ProfileState = {
  data: null,
  isLoading: false,
  uploadProgress: 0,
  error: null,
  isExporting: false,
  isDeleting: false,
};

/**
 * Async Thunk: Fetch Profile
 */
export const fetchProfileAsync = createAsyncThunk<
  UserProfile,
  void,
  { extra: StoreExtraDependencies; rejectValue: string }
>('profile/fetchProfile', async (_, { extra, rejectWithValue }) => {
  try {
    const profile = await extra.profileGateway.getProfile();
    return profile;
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to fetch profile');
  }
});

/**
 * Async Thunk: Update Username
 */
export const updateUsernameAsync = createAsyncThunk<
  void,
  { username: string },
  { extra: StoreExtraDependencies; rejectValue: string }
>('profile/updateUsername', async ({ username }, { extra, rejectWithValue }) => {
  try {
    await extra.profileGateway.updateUsername(username);
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to update username');
  }
});

/**
 * Async Thunk: Upload Avatar (Three-Step Flow)
 */
export const uploadAvatarAsync = createAsyncThunk<
  { avatarUrl: string },
  File,
  { extra: StoreExtraDependencies; rejectValue: string }
>('profile/uploadAvatar', async (file, { extra, dispatch, rejectWithValue }) => {
  try {
    // Step 1: Request presigned URL
    dispatch(setUploadProgress(10));
    const { uploadUrl, key } = await extra.profileGateway.requestAvatarUploadUrl(file.type);

    // Step 2: Upload to S3
    dispatch(setUploadProgress(30));
    await uploadToS3(uploadUrl, file, (progress) => {
      dispatch(setUploadProgress(30 + Math.floor(progress * 0.6))); // 30-90%
    });

    // Step 3: Confirm upload with backend
    dispatch(setUploadProgress(95));
    const { avatarUrl } = await extra.profileGateway.confirmAvatarUpload(key);

    dispatch(setUploadProgress(100));
    return { avatarUrl };
  } catch (error) {
    dispatch(setUploadProgress(0));
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to upload avatar');
  }
});

/**
 * Helper: Upload file to S3 with progress tracking
 */
const uploadToS3 = (
  uploadUrl: string,
  file: File,
  onProgress: (progress: number) => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const progress = (event.loaded / event.total) * 100;
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
};

/**
 * Async Thunk: Delete Avatar
 */
export const deleteAvatarAsync = createAsyncThunk<
  void,
  void,
  { extra: StoreExtraDependencies; rejectValue: string }
>('profile/deleteAvatar', async (_, { extra, rejectWithValue }) => {
  try {
    await extra.profileGateway.deleteAvatar();
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to delete avatar');
  }
});

/**
 * Async Thunk: Add Wallet
 * Returns wallet data from backend to ensure Redux state matches server state
 */
export const addWalletAsync = createAsyncThunk<
  import('@domain/ports/IProfileGateway').Wallet,
  { type: 'ETH' | 'SOL' | 'BTC'; address: string; ensName?: string },
  { extra: StoreExtraDependencies; rejectValue: string }
>('profile/addWallet', async ({ type, address, ensName }, { extra, rejectWithValue }) => {
  try {
    const wallet = await extra.profileGateway.addWallet(type, address, ensName);
    return wallet;
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to add wallet');
  }
});

/**
 * Async Thunk: Delete Wallet
 */
export const deleteWalletAsync = createAsyncThunk<
  void,
  { type: 'ETH' | 'SOL' | 'BTC' },
  { extra: StoreExtraDependencies; rejectValue: string }
>('profile/deleteWallet', async ({ type }, { extra, rejectWithValue }) => {
  try {
    await extra.profileGateway.deleteWallet(type);
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to delete wallet');
  }
});

/**
 * Async Thunk: Toggle Wallet Visibility (with optimistic update)
 */
export const toggleWalletVisibilityAsync = createAsyncThunk<
  boolean,
  { type: 'ETH' | 'SOL' | 'BTC' },
  { extra: StoreExtraDependencies; state: RootState; rejectValue: string }
>('profile/toggleWalletVisibility', async ({ type }, { extra, getState, rejectWithValue }) => {
  try {
    // Extract current visibility BEFORE any updates
    const state = getState();
    const wallet = state.profile.data?.wallets.find((w) => w.type === type);
    const currentVisibility = wallet?.visible ?? true;

    // Calculate NEW visibility (what we want to set it to)
    const newVisibility = !currentVisibility;

    await extra.profileGateway.toggleWalletVisibility(type, newVisibility);

    // Return the new visibility so the fulfilled handler can use it
    return newVisibility;
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to toggle wallet visibility');
  }
});

/**
 * Async Thunk: Add Social Link
 * Returns social link data from backend to ensure Redux state matches server state
 */
export const addSocialLinkAsync = createAsyncThunk<
  import('@domain/ports/IProfileGateway').SocialLink,
  { platform: string; url: string },
  { extra: StoreExtraDependencies; rejectValue: string }
>('profile/addSocialLink', async ({ platform, url }, { extra, rejectWithValue }) => {
  try {
    const socialLink = await extra.profileGateway.addSocialLink(platform, url);
    return socialLink;
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to add social link');
  }
});

/**
 * Async Thunk: Delete Social Link
 */
export const deleteSocialLinkAsync = createAsyncThunk<
  void,
  { platform: string },
  { extra: StoreExtraDependencies; rejectValue: string }
>('profile/deleteSocialLink', async ({ platform }, { extra, rejectWithValue }) => {
  try {
    await extra.profileGateway.deleteSocialLink(platform);
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to delete social link');
  }
});

/**
 * Async Thunk: Toggle Social Link Visibility (with optimistic update)
 */
export const toggleSocialLinkVisibilityAsync = createAsyncThunk<
  boolean,
  { platform: string },
  { extra: StoreExtraDependencies; state: RootState; rejectValue: string }
>('profile/toggleSocialLinkVisibility', async ({ platform }, { extra, getState, rejectWithValue }) => {
  try {
    // Extract current visibility BEFORE any updates
    const state = getState();
    const link = state.profile.data?.socialLinks.find((s) => s.platform === platform);
    const currentVisibility = link?.visible ?? true;

    // Calculate NEW visibility (what we want to set it to)
    const newVisibility = !currentVisibility;

    await extra.profileGateway.toggleSocialLinkVisibility(platform, newVisibility);

    // Return the new visibility so the fulfilled handler can use it
    return newVisibility;
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to toggle social link visibility');
  }
});

/**
 * Async Thunk: Export User Data (GDPR)
 * Requests an export of all user data. User will receive an email within 24 hours.
 */
export const exportUserDataAsync = createAsyncThunk<
  void,
  void,
  { extra: StoreExtraDependencies; rejectValue: string }
>('profile/exportUserData', async (_, { extra, rejectWithValue }) => {
  try {
    await extra.profileGateway.exportUserData();
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to request data export');
  }
});

/**
 * Async Thunk: Delete Account (GDPR)
 * Permanently deletes the user account and all associated data.
 */
export const deleteAccountAsync = createAsyncThunk<
  void,
  void,
  { extra: StoreExtraDependencies; rejectValue: string }
>('profile/deleteAccount', async (_, { extra, rejectWithValue }) => {
  try {
    await extra.profileGateway.deleteAccount();
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to delete account');
  }
});

/**
 * Profile Slice
 */
const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setUploadProgress: (state, action: PayloadAction<number>) => {
      state.uploadProgress = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch Profile
    builder
      .addCase(fetchProfileAsync.pending, (state) => {
        console.log('[Redux] fetchProfileAsync.pending');
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProfileAsync.fulfilled, (state, action) => {
        console.log('[Redux] fetchProfileAsync.fulfilled', {
          profile: action.payload,
          hasWallets: !!action.payload?.wallets,
          walletsLength: action.payload?.wallets?.length,
          hasSocialLinks: !!action.payload?.socialLinks,
        });
        state.isLoading = false;
        state.data = action.payload;
        console.log('[Redux] state.data after fetch:', {
          exists: !!state.data,
          walletsLength: state.data?.wallets?.length,
        });
      })
      .addCase(fetchProfileAsync.rejected, (state, action) => {
        console.log('[Redux] fetchProfileAsync.rejected', action.payload);
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch profile';
      });

    // Update Username
    builder
      .addCase(updateUsernameAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUsernameAsync.fulfilled, (state) => {
        state.isLoading = false;
        // Profile will be refetched to get updated username
      })
      .addCase(updateUsernameAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update username';
      });

    // Upload Avatar
    builder
      .addCase(uploadAvatarAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.uploadProgress = 0;
      })
      .addCase(uploadAvatarAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.uploadProgress = 100;
        if (state.data) {
          state.data.avatar = action.payload.avatarUrl;
        }
      })
      .addCase(uploadAvatarAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.uploadProgress = 0;
        state.error = action.payload || 'Failed to upload avatar';
      });

    // Delete Avatar
    builder
      .addCase(deleteAvatarAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteAvatarAsync.fulfilled, (state) => {
        state.isLoading = false;
        if (state.data) {
          state.data.avatar = undefined;
        }
      })
      .addCase(deleteAvatarAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to delete avatar';
      });

    // Add Wallet
    builder
      .addCase(addWalletAsync.pending, (state, action) => {
        console.log('[Redux] addWalletAsync.pending', action.meta.arg);
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addWalletAsync.fulfilled, (state, action) => {
        console.log('[Redux] addWalletAsync.fulfilled', {
          payload: action.payload,
          meta: action.meta.arg,
        });
        state.isLoading = false;

        // Update Redux state with backend wallet data
        if (state.data) {
          const backendWallet = action.payload; // Wallet data from backend
          const walletIndex = state.data.wallets.findIndex((w) => w.type === backendWallet.type);

          console.log('[Redux] Updating wallet from backend response:', {
            walletIndex,
            backendWallet,
            currentWallets: state.data.wallets.map(w => ({
              type: w.type,
              address: w.address,
              visible: w.visible
            }))
          });

          if (walletIndex >= 0) {
            // Update existing wallet with backend data
            state.data.wallets[walletIndex] = backendWallet;
            console.log('[Redux] Updated existing wallet:', state.data.wallets[walletIndex]);
          } else {
            // Add new wallet from backend
            state.data.wallets.push(backendWallet);
            console.log('[Redux] Added new wallet:', backendWallet);
          }

          console.log('[Redux] Final wallet state:', state.data.wallets.map(w => ({
            type: w.type,
            address: w.address,
            visible: w.visible
          })));
        } else {
          console.error('[Redux] CRITICAL: state.data is null/undefined - wallet update skipped!');
          console.error('[Redux] Profile must be loaded before wallet update');
        }
      })
      .addCase(addWalletAsync.rejected, (state, action) => {
        console.log('[Redux] addWalletAsync.rejected', action.payload);
        state.isLoading = false;
        state.error = action.payload || 'Failed to add wallet';
      });

    // Delete Wallet
    builder
      .addCase(deleteWalletAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteWalletAsync.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteWalletAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to delete wallet';
      });

    // Toggle Wallet Visibility
    builder
      .addCase(toggleWalletVisibilityAsync.fulfilled, (state, action) => {
        // Update state with the new visibility from the thunk
        if (state.data) {
          const wallet = state.data.wallets.find((w) => w.type === action.meta.arg.type);
          if (wallet) {
            // Set to the new visibility calculated by the thunk
            wallet.visible = action.payload;
          }
        }
      })
      .addCase(toggleWalletVisibilityAsync.rejected, (state, action) => {
        state.error = action.payload || 'Failed to toggle wallet visibility';
      });

    // Add Social Link
    builder
      .addCase(addSocialLinkAsync.pending, (state, action) => {
        console.log('[Redux] addSocialLinkAsync.pending', action.meta.arg);
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addSocialLinkAsync.fulfilled, (state, action) => {
        console.log('[Redux] addSocialLinkAsync.fulfilled', {
          payload: action.payload,
          meta: action.meta.arg,
        });
        state.isLoading = false;

        // Update Redux state with backend social link data
        if (state.data) {
          const backendSocialLink = action.payload; // Social link data from backend
          const linkIndex = state.data.socialLinks.findIndex((l) => l.platform === backendSocialLink.platform);

          console.log('[Redux] Updating social link from backend response:', {
            linkIndex,
            backendSocialLink,
            currentSocialLinks: state.data.socialLinks.map(sl => ({
              platform: sl.platform,
              url: sl.url,
              visible: sl.visible
            }))
          });

          if (linkIndex >= 0) {
            // Update existing link with backend data
            state.data.socialLinks[linkIndex] = backendSocialLink;
            console.log('[Redux] Updated existing social link:', state.data.socialLinks[linkIndex]);
          } else {
            // Add new link from backend
            state.data.socialLinks.push(backendSocialLink);
            console.log('[Redux] Added new social link:', backendSocialLink);
          }

          console.log('[Redux] Final social link state:', state.data.socialLinks.map(sl => ({
            platform: sl.platform,
            url: sl.url,
            visible: sl.visible
          })));
        } else {
          console.error('[Redux] CRITICAL: state.data is null/undefined - social link update skipped!');
          console.error('[Redux] Profile must be loaded before social link update');
        }
      })
      .addCase(addSocialLinkAsync.rejected, (state, action) => {
        console.log('[Redux] addSocialLinkAsync.rejected', action.payload);
        state.isLoading = false;
        state.error = action.payload || 'Failed to add social link';
      });

    // Delete Social Link
    builder
      .addCase(deleteSocialLinkAsync.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteSocialLinkAsync.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(deleteSocialLinkAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to delete social link';
      });

    // Toggle Social Link Visibility
    builder
      .addCase(toggleSocialLinkVisibilityAsync.fulfilled, (state, action) => {
        // Update state with the new visibility from the thunk
        if (state.data) {
          // Recreate the socialLinks array to trigger React re-render
          state.data.socialLinks = state.data.socialLinks.map((link) =>
            link.platform === action.meta.arg.platform
              ? { ...link, visible: action.payload }
              : link
          );
        }
      })
      .addCase(toggleSocialLinkVisibilityAsync.rejected, (state, action) => {
        state.error = action.payload || 'Failed to toggle social link visibility';
      });

    // Export User Data (GDPR)
    builder
      .addCase(exportUserDataAsync.pending, (state) => {
        state.isExporting = true;
        state.error = null;
      })
      .addCase(exportUserDataAsync.fulfilled, (state) => {
        state.isExporting = false;
      })
      .addCase(exportUserDataAsync.rejected, (state, action) => {
        state.isExporting = false;
        state.error = action.payload || 'Failed to export user data';
      });

    // Delete Account (GDPR)
    builder
      .addCase(deleteAccountAsync.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteAccountAsync.fulfilled, (state) => {
        state.isDeleting = false;
        // Note: Auth state will be cleared by the view model after successful deletion
      })
      .addCase(deleteAccountAsync.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload || 'Failed to delete account';
      });
  },
});

/**
 * Actions
 */
export const { clearError, setUploadProgress } = profileSlice.actions;

/**
 * Selectors
 */
export const selectProfile = (state: RootState) => state.profile.data;
export const selectProfileLoading = (state: RootState) => state.profile.isLoading;
export const selectProfileError = (state: RootState) => state.profile.error;
export const selectUploadProgress = (state: RootState) => state.profile.uploadProgress;
export const selectIsExporting = (state: RootState) => state.profile.isExporting;
export const selectIsDeleting = (state: RootState) => state.profile.isDeleting;

/**
 * Reducer
 */
export default profileSlice.reducer;
